"use client";

import { useEffect, useState } from "react";
import { Button } from "~/components/v4/themed/Button";
import { toastHandler } from "~/components/ToastHandler";
import { useSession } from "next-auth/react";
import { follow, unfollowUser } from "~/components/v3/follow/_actions";

type FollowUserButtonProps = {
    userId: string;
    isFollowing: boolean;
    variant?: "default" | "secondary" | "tertiary";
    isLoading?: boolean;
    refetch?: () => void;
    className?: string;
};

export const FollowUserButton = ({
    userId,
    isFollowing: initialIsFollowing,
    isLoading,
    refetch,
    className,
}: FollowUserButtonProps) => {
    const { data: session } = useSession();
    const [isFollowing, setIsFollowing] = useState(initialIsFollowing);

    useEffect(() => {
        setIsFollowing(initialIsFollowing);
    }, [initialIsFollowing]);

    if (!session?.user) return null;

    const handleFollow = async () => {
        await toastHandler({
            loadingMessage: "Following...",
            successMessage: "Followed!",
            errorMessage: "Failed to follow",
            operation: async () => {
                await follow({ userId });
                setIsFollowing(true); // Optimistic update
                refetch?.();
            },
        });
    };

    const handleUnfollow = async () => {
        await toastHandler({
            loadingMessage: "Unfollowing...",
            successMessage: "Unfollowed!",
            errorMessage: "Failed to unfollow",
            operation: async () => {
                await unfollowUser({ userId });
                setIsFollowing(false); // Optimistic update
                refetch?.();
            },
        });
    };

    return (
        <Button
            onClick={isFollowing ? handleUnfollow : handleFollow}
            variant={isFollowing ? "tertiary" : "secondary"}
            isLoading={isLoading}
            className={className}
        >
            {isFollowing ? "Following" : "Follow"}
        </Button>
    );
};
